import { FC, useRef, useMemo, useEffect, } from "react";
// 组件
import { PlusOutlined } from "@ant-design/icons";
import { Table } from "antd";
import { ProTable, ProForm, } from '../antd';
import TableRow from "./subpage/row";
import TableCell from "./subpage/cell";
import TotalTable, { TotalTableActionType } from "./subpage/totalTable";
// 方法
import classNames from "classnames";
import VirtualMainTableContext from './context/virtualMainTableContext';
import useEditVirtualMainTablehook from './hook/useEditVirtualMainTablehook';
import columnsHook from './hook/columnsHook';
import { filterDeleteData, formatDataSource, formatRowKey, getRowDataSource, } from "./util";
import { useLayoutContext } from "../PageLayout/context/LayoutContext";
// 常量
import { IObject } from "../constants/interface";
import { ALL_CHILDREN_KEY, EditVirtualMainTableProps, IEditVirtualMainTablePayload, mainFormKey, } from "./interface";
import { STATUS_CREATED_CODE, STATUS_CREATED } from "../constants/constant";
import './style.less';

/** 可编辑大数据主子表 */
const EditVirtualMainTable: FC<EditVirtualMainTableProps> = props => {
  const {
    height, dataSource, defaultExpandAllRows, className, bordered, disabled, style, showAddSubRow, showEmptySubRow,
    actionRef, totalKeys, totalIndex, totalText, trailerRow, onValuesChange, hiddenAddSubRow, formatTotalData,
  } = props;
  const { enumData } = useLayoutContext();
  const tableRef = useRef<HTMLDivElement>(null);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const [form] = ProForm.useForm();
  const { tableDataSource, expandedRowKeys, dispatchEditVirtualMainTable, } = useEditVirtualMainTablehook(flagIndex, dataSource, form, defaultExpandAllRows, showAddSubRow, showEmptySubRow, trailerRow);
  const { tableColumns } = columnsHook(props, dispatchEditVirtualMainTable, tableDataSource, enumData, form,);
  /** 可编辑主子表格展示的数据 */
  const editMainSubTableDataSource = useMemo(() => {
    const newDataSource = filterDeleteData(tableDataSource, hiddenAddSubRow);
    return newDataSource;
  }, [tableDataSource]);
  useEffect(() => {
    handleUpdateTotalData();
  }, [tableDataSource]);
  /** 表单字段值改变 */
  const handleValuesChange = (changedValues: any, values: any) => {
    const [mainKey, changeFormItem] = Object.entries(changedValues)[0];
    const [childrenKey, changeItem] = Object.entries(changeFormItem as any)[0];
    const dispatchData: IEditVirtualMainTablePayload = {
      mainKey,
      childrenKey,
    };
    if (childrenKey === mainFormKey) {
      dispatchData.changeMainItem = changeItem;
    } else {
      dispatchData.changeChildrenItem = changeItem;
    }
    const changeName = Object.keys(changeItem as any)[0];
    onValuesChange?.(changeName, changeItem, mainKey, childrenKey, form, getRowDataSource(mainKey, childrenKey, values[mainKey][childrenKey], tableDataSource));
    dispatchEditVirtualMainTable('change', dispatchData);
  };
  /** 主表展开、合并 */
  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      dispatchEditVirtualMainTable('addExpandedRowKeys', {updateExpandedRowKeys: record.key});
    } else {
      dispatchEditVirtualMainTable('deleteExpandedRowKeys', {updateExpandedRowKeys: record.key});
    }
  };
  /** 添加主表行 */
  const handleMainAdd = (addItem?: IObject | IObject[]) => {
    dispatchEditVirtualMainTable('addRow', {addItem});
  };
  /** 添加子表行 */
  const handleChildrenAdd = (mainKey: string, addItem?: IObject | IObject[]) => {
    dispatchEditVirtualMainTable('addChildren', { mainKey, addItem });
  };
  /** 追加主子表数据 */
  const handleAppend = (appendItem: IObject[]) => {
    dispatchEditVirtualMainTable('append', {appendItem});
  }
  /** 覆盖主子表数据 */
  const handleCover = (addItem?: IObject[]) => {
    dispatchEditVirtualMainTable('cover', {addItem});
  }
  /** 删除 */
  const handleDelete = (record: any) => {
    const { mainKey, childrenKey } = formatRowKey(record.key);
    if (mainKey && childrenKey) {
      dispatchEditVirtualMainTable('deleteChildren', {mainKey, childrenKey: record.key});
      return;
    }
    if (mainKey && !childrenKey) {
      dispatchEditVirtualMainTable('deleteRow', {mainKey});
    }
  };
  /** 提交 */
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      return formatDataSource(tableDataSource, false, true);
    } catch (errorInfo: any) {
      return false;
    }
  };
  /** 清空 */
  const handleEmptyMainChildren = (mainKey: string) => {
    dispatchEditVirtualMainTable('emptyRow', {mainKey});
  };
  /** 清空 */
  const handleEmpty = () => {
    dispatchEditVirtualMainTable('empty');
  };
  /** 替换 */
  const handleChangeDataSource = (data: any[]) => {
    dispatchEditVirtualMainTable('changeData', { dataSource: data, });
  };
  // 获取当前可编辑表的值
  const handleDataSource = (filterDeleteData?: boolean) => {
    return formatDataSource(tableDataSource, filterDeleteData);
  };
  /** 更新统计数据 */
  const handleUpdateTotalData = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const effectiveDataSource = handleDataSource(true);
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  /** 手动传入表格数据进行统计数据更新 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      handleUpdateTotalData();
    }
  };
  /** 更新单行主表表单数据 */
  const handleMainValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const mainName = [mainKey, mainFormKey];
    const formData = form.getFieldValue(mainName);
    form.setFieldValue(mainName, {
      ...formData,
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
    dispatchEditVirtualMainTable('change', {mainKey, changeMainItem: values});
  };
  /** 更新单行主表下y一行子表的表单数据 */
  const handleMainChildrenValues = (childrenKey: string, values: any) => {
    if (childrenKey === undefined) return;
    const recordKey = formatRowKey(childrenKey);
    const childrenName = [recordKey.mainKey, recordKey.childrenKey];
    const formData = form.getFieldValue(childrenName);
    form.setFieldValue(childrenName, {
      ...formData,
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
    dispatchEditVirtualMainTable('change', {
      mainKey: recordKey.mainKey,
      childrenKey: recordKey.childrenKey,
      changeMainItem: values,
    });
  };
  /** 更新单行主表下所有子表的表单数据 */
  const handleMainChildrensValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const childrenData = handleDataSource(true).find(item => item.key === mainKey)?.children;
    if (Array.isArray(childrenData) && childrenData.length) {
      childrenData.forEach(item => {
        handleMainChildrenValues(item.key, values);
      });
      dispatchEditVirtualMainTable('change', {
        mainKey,
        childrenKey: ALL_CHILDREN_KEY,
        changeMainItem: values,
      });
    }
  };
  const classes = useMemo(() => {
    return classNames('edit-virtual-main-table', className);
  }, [className]);
  if (actionRef) {
    actionRef.current = {
      form,
      empty: handleEmpty,
      cover: handleCover,
      append: handleAppend,
      delete: handleDelete,
      submit: handleSubmit,
      addMain: handleMainAdd,
      addChildren: handleChildrenAdd,
      changeTotal: handleChangeTotal,
      getDataSource: handleDataSource,
      setMainValues: handleMainValues,
      changeDataSource: handleChangeDataSource,
      emptyMainChildren: handleEmptyMainChildren,
      setMainChildrenValues: handleMainChildrenValues,
      setMainChildrensValues: handleMainChildrensValues,
    };
  }
  return (
    <VirtualMainTableContext.Provider value={{
      dispatchEditVirtualMainTable,
      form,
      showAddSubRow,
      showEmptySubRow,
      trailerRow,
    }}>
      <div
        ref={tableRef}
        className={classes}
        style={style}
      >
        <ProForm
          component={false}
          form={form}
          name='edit-virtual-main-table-form'
          onValuesChange={handleValuesChange}
        >
          <ProTable
            dataSource={editMainSubTableDataSource}
            columns={tableColumns}
            pagination={false}
            bordered={bordered}
            virtual
            size="small"
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
            }}
            components={{
              body: {
                row: TableRow,
                cell: TableCell,
              },
            }}
            scroll={{
              y: height,
            }}
            // @ts-ignore
            onRow={() => ({
              disabled,
            })}
            summary={() => {
              if (
                Array.isArray(totalKeys) &&
                !!totalKeys.length &&
                Array.isArray(editMainSubTableDataSource) &&
                !!editMainSubTableDataSource.length
              ) {
                return (
                  <Table.Summary fixed>
                    <TotalTable
                      actionRef={totalTableRef}
                      columns={tableColumns}
                      totalKeys={totalKeys}
                      totalIndex={totalIndex}
                      totalText={totalText}
                      formatTotalData={formatTotalData}
                    />
                  </Table.Summary>
                );
              }
            }}
          />
        </ProForm>
      </div>
    </VirtualMainTableContext.Provider>
  );
};

EditVirtualMainTable.defaultProps = {
  height: 350,
  defaultExpandAllRows: true,
  deleteType: 'ALL',
  addIcon: <PlusOutlined />,
  hideAdd: false,
  disabled: false,
  bordered: true,
  showAddSubRow: false,
  addSubRowText: '添加一行',
  showEmptySubRow: true,
  emptySubRowText: '暂无数据',
  operationWidth: 50,
};

export default EditVirtualMainTable;
